<template>
    <div class="c-choice-tile">
        <div class="c-choice-tile__image-and-info">
            <div
                class="c-choice-tile__image-container"
            >
                <div
                    class="c-choice-tile__fade"
                    :class="{
                        'c-choice-tile__fade--in-view': hasEnteredViewport
                    }"
                >
                    <div
                        v-if="hasEnteredViewport && isMounted"
                        class="c-choice-tile__image-container-inner"
                    >
                        <BaseLink
                            :to="productRoute"
                            class="u-display--block c-choice-tile__image-container-inner__link"
                        >
                            <ProductImage
                                :src="choice.imageUrls[0]"
                                :title="displayName"
                                :choiceId="choice.choiceId"
                                :imagePreset="imagePreset"
                                :isLazyImage="true"
                                :shouldDisplayImage="true"
                                :fadeInAfterLoaded="true"
                                :allowFocusOutline="true"
                                class="c-choice-tile__image"
                            />
                        </BaseLink>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="c-choice-tile__fade"
            :class="{
                'c-choice-tile__fade--in-view': hasEnteredViewport,
            }"
        >
            <div class="c-choice-tile__info-box">
                <span class="o-text--caption">{{ price }}</span>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';

import { useFilters } from '~coreModules/core/js/custom-filters';
import { useMounted } from '~coreModules/core/js/composables/useMounted';

import { rentalRouteNames } from '~modules/core/js/router-constants';

import ProductImage from '~coreModules/core/components/ui/ProductImage.vue';

const props = defineProps({
    choice: {
        type: Object,
        required: true,
    },
    displayName: {
        type: String,
        required: true,
    },
    productSlug: {
        type: String,
        required: true,
    },
    imagePreset: {
        type: String,
        required: true,
    },
    showPrice: {
        type: Boolean,
        default: true,
    },
    isPrePurchase: {
        type: Boolean,
        default: false,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});

const filters = useFilters();
const isMounted = useMounted();

const productRoute = computed(() => ({
    name: rentalRouteNames.product,
    params: { slug: props.productSlug },
    query: {
        color: props.choice?.color?.code,
        ...(props.isPrePurchase && {
            view: 'buy',
        }),
    },
}));

const price = computed(() => {
    if (props.showPrice) {
        return filters.i18nFormatDollarsNoCents(props.choice?.prePurchase?.salePrice);
    }
    return null;
});

onMounted(() => {
    isMounted.value = true;
});

</script>

<style lang='scss'>
    .c-choice-tile {
        width: 100%;
        background-color: $placeholder-background;
        position: relative;

        @include css-has-supported {
            &:has(.c-choice-tile__image-container-inner__link.focus-visible:focus[data-focus-visible-added]) {
                outline: $focus-outline;
            }
        }

        &__fade {
            opacity: 0;
            transition: opacity .5s ease;

            &--in-view {
                opacity: 1;
            }
        }

        &__image-and-info {
            position: relative;
        }

        &__image-container {
            position: relative;

            &::before {
                position: relative;
                display: block;
                content: "";
                width: 100%;
                padding-top: 150%;
                background-color: $placeholder-background;
            }
        }

        &__image-container-inner {
            position: absolute;
            top: 0;
            width: 100%;

            &__link.focus-visible:focus[data-focus-visible-added] {
                outline: 0;
            }
        }

        &__image {
            width: 100%
        }

        &__info-box {
            width: 100%;
            height: $nu-spacer-4;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            background-color: $nu-white;
        }
    }
</style>
